// Newsletter archive — App Flow §4: filters, late-joiner locks, empty states.
// UI/UX §5.2/§5.3 — edition list beside the reader from 768px (reactions column ≥1024).
import {
  Button,
  Card,
  EditionCoverCard,
  EmptyState,
  LoadingState,
  PageHeader,
  Section,
  Select,
  SplitView,
  cx,
} from '@roots/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { EditionReaderScreen } from './edition-reader.js';

export function NewsletterScreen() {
  const { editionId } = useParams({ strict: false }) as { editionId?: string };
  const navigate = useNavigate();
  const [familyId, setFamilyId] = useState<string | undefined>();
  const [year, setYear] = useState<string | undefined>();
  const [participantId, setParticipantId] = useState<string | undefined>();

  const { data: families } = useQuery({
    queryKey: ['families'],
    queryFn: () => api.listFamilies(),
  });
  const { data: familyDetail } = useQuery({
    queryKey: ['family', familyId],
    queryFn: () => api.family(familyId as string),
    enabled: Boolean(familyId),
  });

  // §7.2 — the empty state names the next drop date.
  const { data: home } = useQuery({ queryKey: ['home'], queryFn: () => api.home() });
  const nextDrop = (home?.families ?? [])
    .map((f) => f.cycle?.drop_target)
    .filter((d): d is string => Boolean(d))
    .sort()[0];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['editions', familyId, year, participantId],
    queryFn: () =>
      api.editions({ family_id: familyId, year, participant_id: participantId, limit: 10 }),
  });

  const years = [...new Set((data?.editions ?? []).map((e) => e.year_month.slice(0, 4)))];

  const clearFilters = () => {
    setFamilyId(undefined);
    setYear(undefined);
    setParticipantId(undefined);
  };

  const list = (
    <div className="flex flex-col gap-6">
      <PageHeader title="Newsletter" />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 md:grid-cols-1">
        <Select
          aria-label="Family"
          value={familyId ?? ''}
          onChange={(e) => setFamilyId(e.target.value || undefined)}
        >
          <option value="">All families</option>
          {families?.families.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Year"
          value={year ?? ''}
          onChange={(e) => setYear(e.target.value || undefined)}
        >
          <option value="">All years</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>

        {/* decisions_pending.md D-P7 — pets filter by the occasions they appeared in. */}
        {familyDetail && (
          <Select
            aria-label="Participant"
            value={participantId ?? ''}
            onChange={(e) => setParticipantId(e.target.value || undefined)}
          >
            <option value="">Everyone</option>
            {familyDetail.members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name}
              </option>
            ))}
            {familyDetail.pets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} 🐾
              </option>
            ))}
          </Select>
        )}
      </div>

      {isLoading && <LoadingState onRetry={() => void refetch()} />}

      {!isLoading && (data?.editions.length ?? 0) === 0 && (
        <EmptyState
          message={
            familyId || year || participantId
              ? 'No editions match those filters. Try broadening your search.'
              : nextDrop
                ? `Your first edition is on its way! Drop date: ${new Date(nextDrop).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}`
                : 'Your first edition is on its way!'
          }
          actions={
            familyId || year || participantId ? (
              <Button variant="secondary" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : (
              <Button onClick={() => void navigate({ to: '/my-month' })}>Go contribute</Button>
            )
          }
        />
      )}

      {(data?.editions.length ?? 0) > 0 && (
        <Section>
          <div className="flex flex-col gap-4">
            {data?.editions.map((edition) => (
              <div
                key={edition.id}
                className={cx(
                  'rounded-[var(--radius-lg)]',
                  edition.id === editionId &&
                    'outline outline-2 outline-[var(--color-accent-primary)]',
                )}
              >
                <EditionCoverCard
                  theme={edition.theme}
                  monthLabel={edition.cover.month_label}
                  familyName={edition.family_name}
                  isNew={edition.is_new}
                  locked={edition.locked}
                  onOpen={() =>
                    void navigate({
                      to: '/newsletter/$editionId',
                      params: { editionId: edition.id },
                    })
                  }
                />
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );

  return (
    <SplitView
      list={list}
      detail={editionId ? <EditionReaderScreen key={editionId} /> : undefined}
      showDetailOnMobile={Boolean(editionId)}
      wideDetail
      emptyDetail={
        (data?.editions.length ?? 0) > 0 ? (
          <Card className="roots-paper py-16 text-center">
            <p className="type-body">Pick an edition to start reading.</p>
          </Card>
        ) : undefined
      }
    />
  );
}
